import React, { useEffect, useState } from "react";
import { Card, ProgressBar, Button } from "react-bootstrap";
import { BsAlarmFill, BsXCircle } from "react-icons/bs";

interface ReservationTimerProps {
  reservedUntil: Date;
  onExpired: () => void;
  onCancel: () => void;
}

const ReservationTimer: React.FC<ReservationTimerProps> = ({
  reservedUntil,
  onExpired,
  onCancel,
}) => {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);

  useEffect(() => {
    const reservationJson = localStorage.getItem("lessonReservation");
    let creationTime = new Date();

    if (reservationJson) {
      try {
        const reservationData = JSON.parse(reservationJson);
        if (reservationData.createdAt) {
          creationTime = new Date(reservationData.createdAt);
        }
      } catch (error) {
        console.error("Error parsing reservation data:", error);
      }
    }

    const totalSeconds = Math.floor(
      (reservedUntil.getTime() - creationTime.getTime()) / 1000,
    );
    setTotalTime(totalSeconds > 0 ? totalSeconds : 600); // Default to 10 minutes if calculation fails

    const calculateTimeLeft = () => {
      const now = new Date();
      const differenceSeconds = Math.floor(
        (reservedUntil.getTime() - now.getTime()) / 1000,
      );

      if (differenceSeconds <= 0) {
        onExpired();
        return 0;
      }

      return differenceSeconds;
    };

    const initialTimeLeft = calculateTimeLeft();
    setTimeLeft(initialTimeLeft);

    const timerId = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(timerId);
      }
    }, 1000);

    return () => clearInterval(timerId);
  }, [reservedUntil, onExpired]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const progressPercentage = Math.max(
    0,
    Math.min(100, (timeLeft / totalTime) * 100),
  );

  const getVariant = () => {
    if (timeLeft < 60) return "danger";
    if (timeLeft < 180) return "warning";
    return "primary";
  };

  const handleCancelClick = async () => {
    if (window.confirm("Jeste li sigurni da želite poništiti rezervaciju?")) {
      await onCancel();
    }
  };

  if (timeLeft <= 0) {
    return null;
  }

  return (
    <Card className="mb-4 border-0 shadow-sm">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <BsAlarmFill className="text-danger me-2" size={24} />
            <h5 className="m-0">
              Rezervacija termina ističe za:{" "}
              <span className="text-danger fw-bold">
                {formatTime(timeLeft)}
              </span>
            </h5>
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleCancelClick}
            className="d-flex align-items-center"
          >
            <BsXCircle className="me-1" /> Poništi rezervaciju
          </Button>
        </div>
        <ProgressBar
          now={progressPercentage}
          variant={getVariant()}
          className="w-100"
          style={{ height: "8px" }}
        />
      </Card.Body>
    </Card>
  );
};

export default ReservationTimer;
